<!-- Backdrop -->
<div 
  *ngIf="isOpen"
  class="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity"
  (click)="onBackdropClick()"
></div>

<!-- Search Modal -->
<div 
  *ngIf="isOpen"
  class="search-modal fixed top-20 left-0 right-0 z-50 w-full max-w-2xl mx-auto px-4"
  (click)="$event.stopPropagation()"
>
  <div class="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
    
    <!-- Search Input -->
    <div class="flex items-center px-4 py-3 border-b border-gray-200">
      <svg class="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
      </svg>
      
      <input 
        #searchInput
        type="text"
        [(ngModel)]="searchQuery"
        (input)="onSearchInput($event.target.value)"
        (keydown)="onKeyDown($event)"
        placeholder="Rechercher un convive, une action..."
        class="flex-1 text-lg outline-none placeholder:text-gray-400"
        autocomplete="off"
      />

      <div class="flex items-center gap-2 ml-3">
        <kbd class="hidden sm:inline-block px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded">
          ESC
        </kbd>
      </div>
    </div>

    <!-- Results -->
    <div class="max-h-[500px] overflow-y-auto">
      
      <!-- Loading State -->
      <div *ngIf="loading" class="flex items-center justify-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>

      <!-- Results List (when searching) -->
      <div *ngIf="!loading && results.length > 0 && searchQuery.trim().length >= 2" class="py-2">
        <div
          *ngFor="let result of results; let i = index"
          (click)="selectResult(result)"
          [class.search-result-selected]="i === selectedIndex"
          class="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors group"
          [class.bg-primary/5]="i === selectedIndex"
        >
          <div class="flex items-start justify-between">
            <!-- Result Info -->
            <div class="flex items-start flex-1 min-w-0">
              <span class="text-2xl mr-3 flex-shrink-0">{{ result.icon }}</span>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <p class="text-sm font-medium text-gray-900 truncate">
                    {{ result.title }}
                  </p>
                  <span 
                    *ngIf="result.type === 'convive'"
                    class="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full"
                  >
                    Convive
                  </span>
                  <span 
                    *ngIf="result.type === 'action'"
                    class="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full"
                  >
                    Action
                  </span>
                </div>
                <p *ngIf="result.subtitle" class="text-xs text-gray-500 mt-0.5 truncate">
                  {{ result.subtitle }}
                </p>
              </div>
            </div>

            <!-- Actions -->
            <div *ngIf="result.actions && result.actions.length > 0" class="flex items-center gap-1 ml-4 flex-shrink-0">
              <button
                *ngFor="let action of result.actions"
                (click)="executeAction(action, $event)"
                [class.bg-primary]="action.primary"
                [class.text-white]="action.primary"
                [class.bg-gray-100]="!action.primary"
                [class.text-gray-700]="!action.primary"
                [class.hover:bg-primary/90]="action.primary"
                [class.hover:bg-gray-200]="!action.primary"
                class="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                [class.opacity-100]="i === selectedIndex"
              >
                <span class="mr-1">{{ action.icon }}</span>
                {{ action.label }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div *ngIf="!loading && results.length === 0 && searchQuery.trim().length >= 2" class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <p class="text-gray-600 text-sm">Aucun résultat trouvé</p>
        <p class="text-gray-400 text-xs mt-1">Essayez une autre recherche</p>
      </div>

      <!-- Quick Actions (shown when empty) -->
      <div *ngIf="!loading && searchQuery.trim().length < 2" class="py-2">
        <div class="px-4 py-2">
          <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions rapides</p>
        </div>
        <div
          *ngFor="let result of results; let i = index"
          (click)="selectResult(result)"
          [class.search-result-selected]="i === selectedIndex"
          class="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
          [class.bg-primary/5]="i === selectedIndex"
        >
          <div class="flex items-center">
            <span class="text-2xl mr-3">{{ result.icon }}</span>
            <div class="flex-1">
              <p class="text-sm font-medium text-gray-900">{{ result.title }}</p>
              <p class="text-xs text-gray-500 mt-0.5">{{ result.subtitle }}</p>
            </div>
            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer with keyboard shortcuts -->
    <div class="hidden sm:flex items-center justify-between px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
      <div class="flex items-center gap-4">
        <div class="flex items-center gap-1">
          <kbd class="px-1.5 py-0.5 font-semibold bg-white border border-gray-200 rounded">↑</kbd>
          <kbd class="px-1.5 py-0.5 font-semibold bg-white border border-gray-200 rounded">↓</kbd>
          <span class="ml-1">naviguer</span>
        </div>
        <div class="flex items-center gap-1">
          <kbd class="px-1.5 py-0.5 font-semibold bg-white border border-gray-200 rounded">↵</kbd>
          <span class="ml-1">sélectionner</span>
        </div>
      </div>
      <div class="flex items-center gap-1">
        <kbd class="px-1.5 py-0.5 font-semibold bg-white border border-gray-200 rounded">⌘K</kbd>
        <span class="ml-1">pour ouvrir</span>
      </div>
    </div>
  </div>
</div>
